import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { IODataResponse } from '../../../core/models/odata.models';
import { ApiService } from '../../../core/services/api.service';
import { HttpService } from '../../../core/services/http.service';
import { CreateRoleRequest, Rights, Role, Roles, UpdateRoleRequest, User } from '../models/user-management.models';
import { IUserManagementService } from './user-management.interface';

@Injectable({
    providedIn: 'root',
})
export class UserManagementService implements IUserManagementService {
    private readonly httpService = inject(HttpService);
    private readonly apiService = inject(ApiService);

    // User Rights
    getCurrentUserRights(): Observable<Rights> {
        return this.httpService.get$<Rights>(this.apiService.pathUrl.currentUserRights());
    }

    getUserRights(userId: string): Observable<Rights> {
        return this.httpService.get$<Rights>(this.apiService.pathUrl.userRights(userId));
    }

    updateUserRights(userId: string, rights: Rights): Observable<void> {
        return this.httpService.put$<void>(this.apiService.pathUrl.updateUserRights(userId), rights as unknown as object);
    }

    // Rights
    getCustomerRights(): Observable<string[]> {
        return this.httpService.get$<string[]>(this.apiService.pathUrl.rights());
    }

    // Roles
    getCustomerRoles(): Observable<Roles> {
        return this.httpService.get$<IODataResponse<Role>>(this.apiService.pathUrl.roles()).pipe(map(response => response.value));
    }

    createRole(request: CreateRoleRequest): Observable<Role> {
        return this.httpService.post$<Role>(this.apiService.pathUrl.createRole(), request, new HttpParams());
    }

    getRoleById(roleId: string): Observable<Role> {
        return this.httpService.get$<Role>(this.apiService.pathUrl.roleById(roleId));
    }

    updateRole(roleId: string, request: UpdateRoleRequest): Observable<Role> {
        const data = { ...request };
        delete data.version;
        delete data.id;
        const params = this.apiService.params({ version: request.version });
        const paramsString = params.toString();
        const url = paramsString ? `${this.apiService.pathUrl.roleById(roleId)}?${paramsString}` : this.apiService.pathUrl.roleById(roleId);
        return this.httpService.put$<Role>(url, data);
    }

    deleteRole(roleId: string): Observable<void> {
        return this.httpService.delete$<void>(this.apiService.pathUrl.roleById(roleId));
    }

    // Rights management
    createRight(right: string): Observable<string> {
        return this.httpService.post$<string>(this.apiService.pathUrl.rights(), { name: right }, new HttpParams());
    }

    updateRight(rightId: string, right: string): Observable<string> {
        return this.httpService.put$<string>(this.apiService.pathUrl.rightById(rightId), { name: right });
    }

    deleteRight(rightId: string): Observable<void> {
        return this.httpService.delete$<void>(this.apiService.pathUrl.rightById(rightId));
    }

    // User management
    getUsers(): Observable<User[]> {
        return this.httpService.get$<IODataResponse<User>>(this.apiService.pathUrl.users()).pipe(map(response => response.value));
    }

    getSelectorUsers(): Observable<{ id: string; displayName: string }[]> {
        return this.httpService
            .get$<IODataResponse<{ id: string; displayName: string }>>(this.apiService.pathUrl.selectorsUsers())
            .pipe(map(response => response.value));
    }

    getSelectorUsersByIds(userIds: string[]): Observable<{ id: string; displayName: string }[]> {
        if (userIds.length === 0) {
            return of([]);
        }

        const uniqueIds = [...new Set(userIds)];
        const filter = uniqueIds.map(id => `(id eq ${id})`).join(' or ');
        const params = this.apiService.params({
            $filter: filter,
        });
        return this.httpService
            .get$<IODataResponse<{ id: string; displayName: string }>>(this.apiService.pathUrl.selectorsUsers(`&${params.toString()}`))
            .pipe(map(response => response.value));
    }

    getUserById(userId: string): Observable<User> {
        return this.httpService.get$<User>(this.apiService.pathUrl.userById(userId));
    }

    createUser(user: Partial<User>): Observable<User> {
        return this.httpService.post$<User>(this.apiService.pathUrl.users(), user, new HttpParams());
    }

    updateUser(userId: string, user: Partial<User>): Observable<User> {
        return this.httpService.put$<User>(this.apiService.pathUrl.userById(userId), user);
    }

    deleteUser(userId: string): Observable<void> {
        return this.httpService.delete$<void>(this.apiService.pathUrl.userById(userId));
    }

    assignRoleToUser(userId: string, roleIds: string[]): Observable<void> {
        return this.httpService.put$<void>(this.apiService.pathUrl.userRoles(userId), roleIds);
    }

    removeRoleFromUser(userId: string, roleId: string): Observable<void> {
        return this.httpService.delete$<void>(this.apiService.pathUrl.userRoleById(userId, roleId));
    }

    assignRightToUser(userId: string, rightId: string): Observable<void> {
        return this.httpService.post$<void>(this.apiService.pathUrl.userRights(userId), { rightId }, new HttpParams());
    }

    removeRightFromUser(userId: string, rightId: string): Observable<void> {
        return this.httpService.delete$<void>(this.apiService.pathUrl.userRightsById(userId, rightId));
    }
}
